import React, { createContext, type ReactNode, useContext, useMemo, useState, useCallback } from 'react';
import type { mapResultsToSwitcherProps } from '../utils/map-results-to-switcher-props';
import type { OrgWithUnits, ThirdPartyApp } from '../../types';
import { type SwitcherItemType } from '../utils/links';

interface UnifiedAnalyticsConfig {
	cloudId?: string;
	currentOrg?: string;
	currentUnit?: string;
	customLinks?: SwitcherItemType[];
	hasDiscoverySection?: boolean;
	hasSwitcherAnalytics?: boolean;
	organisations?: Map<OrgWithUnits['id'], OrgWithUnits>;
	productSwitcher?: string;
	thirdPartyConfig?: ThirdPartyApp[];
	tryRovoChatCohort?: string;
}

interface UnifiedAnalyticsContextProviderProps {
	children: ReactNode;
	cloudId?: string;
	hasDiscoverySection?: boolean;
	hasSwitcherAnalytics?: boolean;
	productSwitcher?: string;
	switcherLinks?: ReturnType<typeof mapResultsToSwitcherProps>;
}

interface UnifiedAnalyticsContextValue extends UnifiedAnalyticsConfig {
	updateContext?: (updates: Partial<UnifiedAnalyticsConfig>) => void;
}

const UnifiedAnalyticsContext = createContext<UnifiedAnalyticsContextValue | undefined>(undefined);

export const UnifiedAnalyticsContextProvider = ({
	productSwitcher = 'appSwitcher',
	cloudId = 'unknown',
	hasSwitcherAnalytics = false,
	hasDiscoverySection = false,
	switcherLinks = undefined,
	children,
}: UnifiedAnalyticsContextProviderProps): React.JSX.Element => {
	const [values, setValues] = useState<Partial<UnifiedAnalyticsContextValue>>({});

	const handleUpdateContext = useCallback((updates: Partial<UnifiedAnalyticsConfig>) => {
		setValues((prev) => ({ ...prev, ...updates }));
	}, []);

	const contextValue = useMemo<UnifiedAnalyticsContextValue>(
		() => ({
			productSwitcher,
			cloudId,
			hasSwitcherAnalytics,
			currentOrg: switcherLinks?.currentSiteOrgWithUnit?.orgId,
			currentUnit: switcherLinks?.currentSiteOrgWithUnit?.unitId,
			thirdPartyConfig: switcherLinks?.thirdPartyConfig,
			customLinks: switcherLinks?.customLinks,
			organisations: switcherLinks?.organisations,
			hasDiscoverySection: values.hasDiscoverySection ?? hasDiscoverySection,
			updateContext: handleUpdateContext,
			tryRovoChatCohort: switcherLinks?.tryRovoChatCohort,
		}),
		[productSwitcher, cloudId, hasSwitcherAnalytics, switcherLinks, values.hasDiscoverySection, hasDiscoverySection, handleUpdateContext],
	);

	return (
		<UnifiedAnalyticsContext.Provider value={contextValue}>
			{children}
		</UnifiedAnalyticsContext.Provider>
	);
};

export const useUnifiedAnalyticsContext = (): UnifiedAnalyticsContextValue => {
	const context = useContext(UnifiedAnalyticsContext);

	return (
		context ?? {
			productSwitcher: 'appSwitcher',
			cloudId: 'unknown',
			hasSwitcherAnalytics: false,
			thirdPartyConfig: undefined,
			hasDiscoverySection: false,
			customLinks: undefined,
			organisations: undefined,
			currentOrg: undefined,
			currentUnit: undefined,
		}
	);
};

export const useUpdateUnifiedAnalyticsContext = () => {
	const context = useContext(UnifiedAnalyticsContext);
	return context?.updateContext;
};
